import { SizeInfoItem } from './types';

const SIGNIFICANT_DIFFERENCE_THRESHOLD = 10;

export interface ComparisonResult {
  similarity: number[]; // 각 사이즈별 유사도 점수 (점수가 높을수록 유사함)
  highlightFlags: boolean[][]; // [row][col] 하이라이팅 여부 (각 셀이 비교값과 유사한지)
  mostSimilarIndex: number; // 가장 유사한 사이즈의 인덱스 (-1인 경우 유사한 사이즈 없음) - 호환성 유지
  mostSimilarIndices: number[]; // 가장 유사한 사이즈들의 인덱스 배열 (동률 처리용)
}

/**
 * 현재 상품의 실측 사이즈와 비교 대상(내 구매 상품, 직접 입력 등)의 사이즈를 비교 분석합니다.
 *
 * 알고리즘:
 * 1. 각 컬럼별로 비교 대상 값과의 차이(절댓값)를 계산합니다.
 * 2. 각 컬럼에서 차이값이 가장 작은 셀에 하이라이팅 표시
 * 3. 차이값이 동일한 경우 모두 하이라이팅
 * 4. 차이값이 10 이상인 경우 하이라이팅 제외
 * 5. 각 옵션별 하이라이팅 개수 계산
 * 6. 하이라이팅 개수가 가장 많은 옵션에 "추천" 라벨 표시 (하이라이팅 자체는 모두 유지)
 * 7. 동률인 경우 모두 "추천" 라벨 표시
 *
 * @param item 현재 상품의 실측 사이즈 정보
 * @param compareRow 비교할 대상의 실측 데이터 행
 * @returns 비교 분석 결과 (유사도 점수, 하이라이팅 플래그, 추천 인덱스)
 */
export const analyzeSizeSimilarity = (
  item: SizeInfoItem | undefined,
  compareRow: string[] | undefined,
): ComparisonResult | null => {
  if (!item || !compareRow || item.rows.length === 0) return null;

  const bodyRows = item.rows;

  // 비교 상품 값 파싱 (숫자가 아닌 경우 제외, 0인 경우 제외, 첫 번째 컬럼(라벨) 제외)
  const targetValues = compareRow.slice(1).map((v) => {
    const num = parseFloat(String(v));
    return isNaN(num) || num === 0 ? null : num;
  });

  // 하이라이팅 플래그 초기화
  const highlightFlags: boolean[][] = bodyRows.map(() => new Array(item.headers.length).fill(false));

  for (let col = 1; col < item.headers.length; col++) {
    const targetVal = targetValues[col - 1];
    if (targetVal === null) continue;

    let minDiff = Infinity;
    let closestRowIndices: number[] = [];

    // 1. 해당 컬럼에서 가장 차이가 적은 값 찾기
    bodyRows.forEach((row, rowIndex) => {
      const cellVal = parseFloat(row[col]);
      if (isNaN(cellVal)) return;

      const diff = Math.abs(cellVal - targetVal);

      if (diff >= SIGNIFICANT_DIFFERENCE_THRESHOLD) return;

      if (diff < minDiff) {
        minDiff = diff;
        closestRowIndices = [rowIndex];
      } else if (diff === minDiff) {
        closestRowIndices.push(rowIndex);
      }
    });

    // 2. 최소 차이값을 가진 셀들에 하이라이팅 표시 (모든 옵션에 유지)
    if (minDiff < Infinity) {
      closestRowIndices.forEach((rowIndex) => {
        highlightFlags[rowIndex][col] = true;
      });
    }
  }

  // 3. 각 옵션별 하이라이팅 개수 계산
  const highlightCounts = highlightFlags.map((rowFlags) =>
    rowFlags.slice(1).filter((flag) => flag).length, // 0번째 컬럼(라벨)은 제외하고 계산
  );

  // 4. 하이라이팅 개수가 가장 많은 값 찾기
  const maxHighlightCount = Math.max(...highlightCounts);

  // 5. 하이라이팅 개수가 가장 많은 옵션들의 인덱스 (동률 처리) → "추천" 라벨 대상
  const mostSimilarIndices: number[] = [];
  if (maxHighlightCount > 0) {
    highlightCounts.forEach((count, index) => {
      if (count === maxHighlightCount) {
        mostSimilarIndices.push(index);
      }
    });
  }

  // 6. 추천 대상 옵션들의 라벨(0번째 컬럼)을 하이라이팅 처리
  mostSimilarIndices.forEach((rowIndex) => {
    highlightFlags[rowIndex][0] = true;
  });

  // 호환성을 위해 mostSimilarIndex는 첫 번째 인덱스 사용
  const mostSimilarIndex = mostSimilarIndices.length > 0 ? mostSimilarIndices[0] : -1;

  return {
    similarity: highlightCounts,
    highlightFlags,
    mostSimilarIndex,
    mostSimilarIndices,
  };
};
